import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';

import { STORAGE_KEYS } from '../components/dynamic-inputs/util/constants';
import { SessionStorageService } from './session-storage.service';

const ACCURACY_THRESHOLD_METERS = 200;
const TIMEOUT_MS = 10000;
const MIN_OVERLAY_DISPLAY_MS = 2000;

@Injectable({ providedIn: 'root' })
export class GeolocationService {

  pendingGeolocation: Subject<boolean> = new Subject<boolean>();
  loading$ = new BehaviorSubject<boolean>(false);

  private watchId: number | null = null;
  private bestFix: GeolocationPosition | null = null;
  private timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  private currentSession = 0;
  private overlayShownAt: number | null = null;

  constructor(
    private localStorageService: SessionStorageService,
    private router: Router,
  ) {
  }

  public getPosition = (): void => {
    if (!navigator.geolocation) {
      this.pendingGeolocation.next(true);
      return;
    }

    // Reset any previous session (handles concurrent getPosition calls).
    this.cleanupWatchAndTimeout();
    this.showLoadingOverlay();

    this.currentSession += 1;
    const mySession = this.currentSession;
    this.bestFix = null;

    const onSuccess = (position: GeolocationPosition): void => {
      if (mySession !== this.currentSession) {
        return;
      }
      const { accuracy } = position.coords;
      if (accuracy <= ACCURACY_THRESHOLD_METERS) {
        this.acceptFix(position, mySession);
        return;
      }
      if (this.bestFix === null || accuracy < this.bestFix.coords.accuracy) {
        this.bestFix = position;
      }
    };

    const onError = (error: GeolocationPositionError): void => {
      if (mySession !== this.currentSession) {
        return;
      }
      if (error.code === 1) {
        this.runWhenMinElapsed(mySession, () => {
          this.cleanupWatchAndTimeout();
          this.pendingGeolocation.next(true);
        });
      }
      // POSITION_UNAVAILABLE (2) and TIMEOUT (3) at the geolocation level:
      // keep watching until our own 10s timeout decides.
    };

    this.watchId = navigator.geolocation.watchPosition(onSuccess, onError, {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: TIMEOUT_MS,
    });

    this.timeoutHandle = setTimeout(() => {
      if (mySession !== this.currentSession) {
        return;
      }
      if (this.bestFix !== null) {
        this.acceptFix(this.bestFix, mySession);
      } else {
        this.runWhenMinElapsed(mySession, () => {
          this.cleanupWatchAndTimeout();
          this.pendingGeolocation.next(true);
        });
      }
    }, TIMEOUT_MS);
  }

  private acceptFix(position: GeolocationPosition, sessionAtSchedule: number): void {
    this.runWhenMinElapsed(sessionAtSchedule, () => {
      const { latitude, longitude, accuracy } = position.coords;
      this.cleanupWatchAndTimeout();
      this.localStorageService.saveSessionEntry(STORAGE_KEYS.POSITION, {
        latitude,
        longitude,
        accuracy,
      });
      this.pendingGeolocation.next(false);
    });
  }

  private cleanupWatchAndTimeout(): void {
    this.hideLoadingOverlay();
    if (this.watchId !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(this.watchId);
    }
    this.watchId = null;
    if (this.timeoutHandle !== null) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }
    this.bestFix = null;
  }

  private showLoadingOverlay(): void {
    this.overlayShownAt = Date.now();
    this.loading$.next(true);
  }

  private hideLoadingOverlay(): void {
    this.loading$.next(false);
    this.overlayShownAt = null;
  }

  private runWhenMinElapsed(sessionAtSchedule: number, action: () => void): void {
    const shownAt = this.overlayShownAt;
    const run = (): void => {
      if (sessionAtSchedule !== this.currentSession) {
        return;
      }
      action();
    };
    if (shownAt === null) {
      run();
      return;
    }
    const remaining = MIN_OVERLAY_DISPLAY_MS - (Date.now() - shownAt);
    if (remaining <= 0) {
      run();
      return;
    }
    setTimeout(run, remaining);
  }
}
